// =============================================================================
// Harness Capability Types
// =============================================================================

/**
 * The runtimes a chat can be started on. `cohesive` is the legacy loop and is always present;
 * the rest are agent harnesses, offered only where the harness runtime is enabled.
 */
export const HARNESS_IDS = {
  COHESIVE: "cohesive",
  CLAUDE_CODE: "claude-code",
  OPENCODE: "opencode",
  CODEX: "codex",
} as const;

export type HarnessId = (typeof HARNESS_IDS)[keyof typeof HARNESS_IDS];

/**
 * How far a live session may move its model. `same_provider` means the session is pinned to the
 * provider it started on — opencode, whose sandbox resolves its provider credentials once — so
 * the selector can disable the rest rather than let the user discover the 400.
 */
export const MODEL_SWITCH_SCOPES = {
  ANY: "any",
  SAME_PROVIDER: "same_provider",
} as const;

export type ModelSwitchScope =
  (typeof MODEL_SWITCH_SCOPES)[keyof typeof MODEL_SWITCH_SCOPES];

/**
 * Who pays for a turn on a model, from the credential its provider resolves to. About who pays,
 * never whether it works — that is the harness's `available`.
 */
export const MODEL_FUNDING = {
  SUBSCRIPTION: "subscription",
  API_KEY: "api_key",
  PLATFORM: "platform",
} as const;

export type ModelFunding = (typeof MODEL_FUNDING)[keyof typeof MODEL_FUNDING];

/** One reasoning effort a model accepts, in the catalog's vocabulary (none … max). */
export interface HarnessCapabilityEffort {
  id: string;
  label: string;
  description: string;
}

/** One selectable model. The id is the harness's own vocabulary, not a shared model identity. */
export interface HarnessCapabilityModel {
  id: string;
  label: string;
  /** Absent where the user resolves nothing for this model's provider. */
  funding?: ModelFunding;
  /** Empty or absent for a model that takes no effort. */
  efforts?: HarnessCapabilityEffort[];
  default_effort_id?: string;
}

/**
 * One runtime a user may start a chat on. Server-owned: `available` is derived from the
 * credentials this user resolves, and `unavailable_reason` is the sentence to show when it is
 * false — never restate either client-side.
 *
 * `id` is typed as a string rather than `HarnessId` because the catalog is the server's: a
 * harness added there must render, not crash the selector.
 */
export interface HarnessCapability {
  id: string;
  label: string;
  available: boolean;
  unavailable_reason?: string;
  /** Empty for cohesive, whose dial is the reasoning effort tier rather than a model. */
  models: HarnessCapabilityModel[];
  default_model_id?: string;
  /**
   * The model and effort a new turn defaults to under the user's preferences; `default_model_id`
   * is the catalog's.
   */
  resolved_default_model_id?: string;
  resolved_default_effort_id?: string;
  /** Absent on cohesive, which has no models to switch between. */
  model_switch_scope?: ModelSwitchScope;
}

export interface HarnessCapabilitiesResponse {
  data: {
    harness_capabilities: HarnessCapability[];
  };
}
